import Phaser from "phaser";
import { GAME, HERO } from "@/config";
import { PALETTE, PALETTE_HEX } from "@/palette";
import { artTextureKey, isArtLoaded } from "@/sprites/artManifest";
import { runtime } from "@/systems/runtime";
import { DEFAULT_FACILITY_NAME } from "@/data/tunables";

/**
 * The shared per-scene hero banner (v0.7 "The Look"). Every scene draws the same
 * banner — full-width teal band + mustard sunburst + coral starbursts + the
 * costumed owner bust + a title logotype + a motto pill + the facility nameplate.
 * The DOM "wings" in ui/dom.ts extend the band to the true screen edges.
 *
 * Parameterised per scene: which title logotype, which owner costume, and the
 * (per-tier gag) motto. See OfficeScene / TierScene / AlumniScene for callers.
 */
/** Band + sunburst + starburst colours — alternated per scene so no two heroes
 *  look the same (adjacent scenes always differ). */
export interface HeroTheme {
  band: number;
  burst: number;
  star: number;
}

export const HERO_THEME: Record<string, HeroTheme> = {
  office: { band: PALETTE.teal, burst: PALETTE.mustard, star: PALETTE.coralRed },
  detox: { band: PALETTE.coralRed, burst: PALETTE.paper, star: PALETTE.teal },
  residential: { band: PALETTE.green, burst: PALETTE.mustard, star: PALETTE.coralRed },
  php: { band: PALETTE.mustard, burst: PALETTE.teal, star: PALETTE.coralRed },
  iop: { band: PALETTE.teal, burst: PALETTE.coralRed, star: PALETTE.paper },
  sober: { band: PALETTE.coralRed, burst: PALETTE.paper, star: PALETTE.teal },
  alumni: { band: PALETTE.green, burst: PALETTE.mustard, star: PALETTE.coralRed },
};

export interface HeroConfig {
  /** manifest key for the title logotype (e.g. "hero-detox"). */
  titleKey: string;
  /** styled-text fallback when the logotype art is absent. */
  titleText: string;
  /** manifest key for the costumed owner bust (e.g. "owner-detox"). */
  ownerKey: string;
  /** the motto pill line (per-tier gag). */
  motto: string;
  /** band / sunburst / starburst colours (see HERO_THEME). */
  theme: HeroTheme;
}

const D = 9000; // the hero draws above the scene contents

/** Set the facility-name bar to the current name, auto-shrunk to fit the bar. */
export function fitFacilityName(text: Phaser.GameObjects.Text): void {
  const name = runtime.state.account.facilityName ?? DEFAULT_FACILITY_NAME;
  if (text.text === name) return;
  text.setScale(1).setText(name);
  const maxW = GAME.width - 140; // inside the inset nameplate
  if (text.width > maxW) text.setScale(maxW / text.width);
}

/**
 * Draw the hero banner into `scene`. Returns the facility-name Text so the
 * caller can re-sync it (a rename made while the scene is live).
 */
export function buildHero(scene: Phaser.Scene, cfg: HeroConfig): Phaser.GameObjects.Text {
  const HX = 0;
  const HY = HERO.y;
  const HW = GAME.width;
  const HH = HERO.h;
  const BR = HERO.border;

  // teal band + charcoal top/bottom rules (full width, flat — the wings continue it)
  const base = scene.add.graphics().setDepth(D).setScrollFactor(0);
  base.fillStyle(cfg.theme.band, 1);
  base.fillRect(HX, HY, HW, HH);
  base.fillStyle(PALETTE.charcoal, 1);
  base.fillRect(HX, HY, HW, BR);
  base.fillRect(HX, HY + HH - BR, HW, BR);

  // mustard sunburst — wedge tips clipped to an INSET rect so the far L/R edges
  // stay plain teal (the wings join seamlessly); fills to the rules above/below.
  const EDGE_TEAL = HERO.edgeSafe;
  const clipL = HX + EDGE_TEAL;
  const clipR = HX + HW - EDGE_TEAL;
  const clipT = HY + BR;
  const clipB = HY + HH - BR;
  const focalX = HX + HW * 0.72;
  const focalY = HY + HH * 0.5;
  const cx = focalX;
  const cy = focalY;
  const edge = (ang: number): [number, number] => {
    const dx = Math.cos(ang);
    const dy = Math.sin(ang);
    let t = Infinity;
    if (dx > 1e-6) t = Math.min(t, (clipR - cx) / dx);
    else if (dx < -1e-6) t = Math.min(t, (clipL - cx) / dx);
    if (dy > 1e-6) t = Math.min(t, (clipB - cy) / dy);
    else if (dy < -1e-6) t = Math.min(t, (clipT - cy) / dy);
    return [cx + dx * t, cy + dy * t];
  };
  const sun = scene.add.graphics().setDepth(D).setScrollFactor(0);
  sun.fillStyle(cfg.theme.burst, 1);
  const N = 24;
  for (let i = 0; i < N; i += 2) {
    const [x1, y1] = edge((i / N) * Math.PI * 2);
    const [x2, y2] = edge(((i + 1) / N) * Math.PI * 2);
    sun.beginPath();
    sun.moveTo(cx, cy);
    sun.lineTo(x1, y1);
    sun.lineTo(x2, y2);
    sun.closePath();
    sun.fillPath();
  }

  // coral atomic starbursts
  const star = scene.add.graphics().setDepth(D + 1).setScrollFactor(0);
  for (const [sx, sy, sr] of [
    [HX + 470, HY + 76, 30],
    [HX + HW - 130, HY + 100, 42],
    [HX + HW - 290, HY + HH - 76, 24],
    [HX + 330, HY + HH - 62, 22],
  ] as const) {
    star.lineStyle(7, cfg.theme.star, 1);
    star.fillStyle(cfg.theme.star, 1);
    for (let k = 0; k < 8; k++) {
      const a = (k / 8) * Math.PI * 2;
      star.beginPath();
      star.moveTo(sx - Math.cos(a) * sr, sy - Math.sin(a) * sr);
      star.lineTo(sx + Math.cos(a) * sr, sy + Math.sin(a) * sr);
      star.strokePath();
      star.fillCircle(sx + Math.cos(a) * sr, sy + Math.sin(a) * sr, 8);
    }
    star.fillCircle(sx, sy, 12);
  }

  // costumed owner bust, centred just left of the sunburst epicenter
  if (isArtLoaded(cfg.ownerKey)) {
    const b = scene.add
      .image(focalX - 35, focalY, artTextureKey(cfg.ownerKey))
      .setOrigin(0.5, 0.5)
      .setDepth(D + 1)
      .setScrollFactor(0);
    b.setScale(Math.min((HH - 24) / b.height, (HW * 0.46) / b.width));
  }

  // title logotype + motto pill share ONE column centre
  const LEFT_CX = HX + 380;
  const pillW = 560;
  const pillH = 66;
  const pillY = HY + HH - 94;
  const titleCY = (HY + BR + pillY) / 2;
  if (isArtLoaded(cfg.titleKey)) {
    const logo = scene.add
      .image(LEFT_CX, titleCY, artTextureKey(cfg.titleKey))
      .setOrigin(0.5, 0.5)
      .setDepth(D + 2)
      .setScrollFactor(0);
    logo.setScale(Math.min(660 / logo.width, 250 / logo.height));
  } else {
    scene.add
      .text(LEFT_CX, titleCY, cfg.titleText, {
        fontFamily: "Georgia, serif",
        fontSize: "110px",
        fontStyle: "bold",
        color: PALETTE_HEX.paper,
        stroke: PALETTE_HEX.charcoal,
        strokeThickness: 14,
        align: "center",
        wordWrap: { width: 640 },
      })
      .setOrigin(0.5)
      .setDepth(D + 2)
      .setScrollFactor(0);
  }

  // motto pill — centred under the title (same LEFT_CX)
  const pg = scene.add.graphics().setDepth(D + 2).setScrollFactor(0);
  pg.fillStyle(PALETTE.paper, 1);
  pg.lineStyle(5, PALETTE.charcoal, 1);
  pg.fillRoundedRect(LEFT_CX - pillW / 2, pillY, pillW, pillH, 33);
  pg.strokeRoundedRect(LEFT_CX - pillW / 2, pillY, pillW, pillH, 33);
  const mottoText = scene.add
    .text(LEFT_CX, pillY + pillH / 2, cfg.motto, {
      fontFamily: "Georgia, serif",
      fontSize: "33px",
      fontStyle: "bold",
      color: PALETTE_HEX.charcoal,
    })
    .setOrigin(0.5)
    .setDepth(D + 3)
    .setScrollFactor(0);
  const mottoMax = pillW - 40;
  if (mottoText.width > mottoMax) mottoText.setScale(mottoMax / mottoText.width);

  // facility-name nameplate — thin charcoal strip flush UNDER the hero, inset
  // 50px, rounded bottom corners; name auto-fit.
  const barX = 50;
  const barW = GAME.width - 100;
  const barY = HY + HH;
  const barH = 64;
  scene.add
    .graphics()
    .setDepth(D + 5)
    .setScrollFactor(0)
    .fillStyle(PALETTE.charcoal, 1)
    .fillRoundedRect(barX, barY, barW, barH, { tl: 0, tr: 0, bl: 22, br: 22 });
  const fac = scene.add
    .text(barX + barW / 2, barY + barH / 2, "", {
      fontFamily: "Georgia, serif",
      fontSize: "36px",
      fontStyle: "bold",
      color: PALETTE_HEX.paper,
    })
    .setOrigin(0.5)
    .setDepth(D + 6)
    .setScrollFactor(0);
  fitFacilityName(fac);
  return fac;
}
